/** Verwaltung (nur Admin, nur online). */
import { api } from '../api.js';
import { $, dayLabel, errorText, esc, formValues, toast } from '../ui.js';
import { renderEntryForm } from './entries.js';
import { period, STATUS_LABEL } from './absences.js';
import type { AbsenceDTO, EntryDTO, UserDTO } from '../../shared/types.js';
import type { VacationSummary } from '../../shared/vacation.js';
import { formatEuro, type Earnings, type WageRule } from '../../shared/wage.js';
import { formatPrograms, parsePrograms, runPhase, type ApplianceAdminDTO, type RunDTO } from '../../shared/appliances.js';
import { berlinDate, berlinTime, formatHM, formatNumberDe, monthLabel, monthOf, shiftMonth, workMinutes } from '../../shared/time.js';

type AdminUser = UserDTO & { inviteCode: string | null; inviteExpires: number | null; lockedUntil: number | null };

function tabs(active: string): string {
  const t = [
    ['monat', 'Monat'], ['mitarbeiter', 'Mitarbeiter'], ['antraege', 'Anträge'], ['taetigkeiten', 'Tätigkeiten'], ['geraete', 'Geräte'], ['protokoll', 'Protokoll'],
  ];
  return `<nav class="tabs">${t.map(([k, l]) => `<a href="#/admin/${k}" class="${k === active ? 'active' : ''}">${l}</a>`).join('')}</nav>`;
}

async function guard(el: HTMLElement, fn: () => Promise<void>): Promise<void> {
  try {
    await fn();
  } catch (err) {
    el.innerHTML += `<div class="alert error">${esc(errorText(err))}</div>`;
  }
}

export async function renderAdmin(el: HTMLElement, parts: string[]): Promise<void> {
  const [section = 'monat', a, b] = parts;
  el.innerHTML = tabs(section) + '<div id="admin-body"><p class="muted">Lädt …</p></div>';
  const body = $('#admin-body', el);
  await guard(body, async () => {
    switch (section) {
      case 'mitarbeiter':
        return renderUsers(body);
      case 'antraege':
        return renderRequests(body);
      case 'taetigkeiten':
        return renderActivities(body);
      case 'protokoll':
        return renderAudit(body);
      case 'geraete':
        return renderAppliancesAdmin(body, new URLSearchParams(a ?? ''));
      case 'eintraege':
        return renderUserEntries(body, Number(a), b ?? monthOf(berlinDate(Date.now())));
      case 'eintrag':
        return renderAdminEntry(body, Number(a), b);
      default:
        return renderMonth(body, a ?? shiftMonth(monthOf(berlinDate(Date.now())), 0));
    }
  });
}

// ---------- Monatsübersicht, Freigabe, Export ----------

interface OverviewRow {
  user: UserDTO;
  entries: number;
  running: boolean;
  minutes: number;
  late: number;
  vacationDays: number;
  sickDays: number;
  locked: boolean;
  earnings: Earnings | null;
}

function earningsWarn(e: Earnings | null): string {
  if (!e) return '';
  const out: string[] = [`<br><small class="muted">voraussichtlich ${formatEuro(e.gross)}</small>`];
  if (e.exceeded) out.push(`<span class="badge err">Minijob-Grenze ${formatEuro(e.limit!)} überschritten</span>`);
  else if (e.targetExceeds) out.push('<span class="badge wait" title="Soll-Stunden × Stundenlohn liegt über der Minijob-Grenze">Soll über Minijob-Grenze</span>');
  else if (e.limit !== null && e.remainingMinutes !== null && e.remainingMinutes < 5 * 60) out.push(`<span class="badge wait">noch ${formatHM(e.remainingMinutes)} Std. bis Grenze</span>`);
  if (e.belowMinWage) out.push(`<span class="badge err">Stundenlohn unter Mindestlohn ${formatEuro(e.minWage!)}</span>`);
  return out.join(' ');
}

async function renderMonth(el: HTMLElement, month: string): Promise<void> {
  const r = await api<{ rows: OverviewRow[]; pendingAbsences: number }>(`/admin/overview?month=${month}`, { fresh: true });
  el.innerHTML = `
    <div class="month-nav">
      <a class="btn icon" href="#/admin/monat/${shiftMonth(month, -1)}" aria-label="Vormonat">‹</a>
      <h1>${esc(monthLabel(month))}</h1>
      <a class="btn icon" href="#/admin/monat/${shiftMonth(month, 1)}" aria-label="Folgemonat">›</a>
    </div>
    ${r.pendingAbsences ? `<a class="alert" href="#/admin/antraege">${r.pendingAbsences === 1 ? "1 offener Urlaubsantrag" : `${r.pendingAbsences} offene Urlaubsanträge`}</a>` : ''}
    <div class="table-wrap"><table class="grid">
      <thead><tr><th>Mitarbeiter</th><th class="r">Stunden</th><th class="r">Soll</th><th class="r">Urlaub</th><th class="r">Krank</th><th>Status</th><th>Export</th></tr></thead>
      <tbody>${r.rows
        .map((row) => `<tr>
          <td><a href="#/admin/eintraege/${row.user.id}/${month}">${esc(row.user.name)}</a>
            ${row.running ? '<span class="badge wait">stempelt</span>' : ''}
            ${row.late ? `<span class="badge late" title="Nachträge später als 7 Tage">${row.late}× Nachtrag</span>` : ''}
            ${earningsWarn(row.earnings)}</td>
          <td class="r">${formatHM(row.minutes)}</td>
          <td class="r">${row.user.targetHoursMonth ?? '–'}</td>
          <td class="r">${formatNumberDe(row.vacationDays, 1)}</td>
          <td class="r">${formatNumberDe(row.sickDays, 1)}</td>
          <td><button class="btn small ${row.locked ? '' : 'primary'}" data-lock="${row.user.id}" data-locked="${row.locked ? 1 : 0}">
            ${row.locked ? 'Freigegeben ✓ – öffnen' : 'Freigeben'}</button></td>
          <td class="nowrap"><a class="btn small" target="_blank" rel="noopener" href="/api/admin/export/${row.user.id}/${month}.html">Druck</a>
            <a class="btn small" href="/api/admin/export/${row.user.id}/${month}.csv">CSV</a></td>
        </tr>`)
        .join('')}</tbody>
    </table></div>
    <button class="btn" id="lock-all">Alle für ${esc(monthLabel(month))} freigeben</button>
    <p class="muted small">Freigabe sperrt den Monat: Mitarbeiter können dann nichts mehr ändern, die Verwaltung schon (mit Protokoll).
    Danach Stundenzettel drucken bzw. CSV in Lexoffice Lohn übernehmen.</p>
  `;
  el.querySelectorAll<HTMLButtonElement>('[data-lock]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      const locked = btn.dataset.locked === '1';
      if (locked && !confirm('Monat wieder öffnen? Der Mitarbeiter kann dann wieder ändern.')) return;
      await api('/admin/locks', { method: 'POST', body: { month, userIds: [Number(btn.dataset.lock)], locked: !locked } });
      void renderMonth(el, month);
    }),
  );
  $('#lock-all', el).addEventListener('click', async () => {
    if (!confirm(`Alle Mitarbeiter für ${monthLabel(month)} freigeben und sperren?`)) return;
    await api('/admin/locks', { method: 'POST', body: { month, userIds: r.rows.map((x) => x.user.id), locked: true } });
    void renderMonth(el, month);
  });
}

async function renderUserEntries(el: HTMLElement, userId: number, month: string): Promise<void> {
  const [u, r] = await Promise.all([
    api<{ users: AdminUser[] }>('/admin/users', { fresh: true }),
    api<{ entries: EntryDTO[]; locked: boolean }>(`/admin/entries?userId=${userId}&month=${month}&deleted=1`, { fresh: true }),
  ]);
  const user = u.users.find((x) => x.id === userId);
  const live = r.entries.filter((e) => !e.deleted);
  const total = live.filter((e) => e.end).reduce((s, e) => s + workMinutes(e.date, e.start, e.end!, e.pauseMin), 0);
  el.innerHTML = `
    <div class="month-nav">
      <a class="btn icon" href="#/admin/eintraege/${userId}/${shiftMonth(month, -1)}" aria-label="Vormonat">‹</a>
      <h1>${esc(user?.name ?? '?')}<br><small>${esc(monthLabel(month))}</small></h1>
      <a class="btn icon" href="#/admin/eintraege/${userId}/${shiftMonth(month, 1)}" aria-label="Folgemonat">›</a>
    </div>
    <p><strong>${formatHM(total)} Std.</strong> ${r.locked ? '<span class="badge ok">freigegeben</span>' : '<span class="badge wait">offen</span>'}</p>
    <div class="table-wrap"><table class="grid">
      <thead><tr><th>Tag</th><th>Zeit</th><th class="r">Pause</th><th class="r">Dauer</th><th>Tätigkeit</th><th></th></tr></thead>
      <tbody>${r.entries
        .map((e) => `<tr class="${e.deleted ? 'deleted' : ''}">
          <td>${esc(dayLabel(e.date))}</td><td class="nowrap">${esc(e.start)}–${esc(e.end ?? 'läuft')}</td>
          <td class="r">${e.pauseMin}</td>
          <td class="r">${e.end ? formatHM(workMinutes(e.date, e.start, e.end, e.pauseMin)) : ''}</td>
          <td>${esc(e.activity)}${e.note ? `<br><small>${esc(e.note)}</small>` : ''}
            ${e.late ? '<span class="badge late">Nachtrag</span>' : ''}${e.deleted ? '<span class="badge err">gelöscht</span>' : ''}</td>
          <td><a class="btn small" href="#/admin/eintrag/${userId}/${esc(e.id)}">Bearbeiten</a></td></tr>`)
        .join('')}</tbody>
    </table></div>
    <a class="btn primary" href="#/admin/eintrag/${userId}/neu">+ Eintrag für ${esc(user?.short ?? '')}</a>
    <a class="btn" target="_blank" rel="noopener" href="/api/admin/export/${userId}/${month}.html">Stundenzettel drucken</a>
    <a class="btn" href="/api/admin/export/${userId}/${month}.csv">CSV</a>
  `;
}

async function renderAdminEntry(el: HTMLElement, userId: number, id: string | undefined): Promise<void> {
  const [u, me] = await Promise.all([
    api<{ users: AdminUser[] }>('/admin/users', { fresh: true }),
    api<{ activities: string[] }>('/me', { fresh: true }),
  ]);
  const user = u.users.find((x) => x.id === userId);
  let entry: EntryDTO | undefined;
  let audit: unknown[] = [];
  if (id && id !== 'neu') {
    const r = await api<{ entry: EntryDTO; audit: unknown[] }>(`/admin/entries/${encodeURIComponent(id)}`, { fresh: true });
    entry = r.entry;
    audit = r.audit;
  }
  const back = () => {
    location.hash = `#/admin/eintraege/${userId}/${monthOf(entry?.date ?? berlinDate(Date.now()))}`;
  };
  await renderEntryForm(
    el,
    entry?.id ?? null,
    { admin: { userId, userName: user?.name ?? '?', activities: me.activities, onDone: back, audit } },
    entry,
  );
}

// ---------- Mitarbeiter ----------

async function renderUsers(el: HTMLElement): Promise<void> {
  const r = await api<{ users: AdminUser[] }>('/admin/users', { fresh: true });
  const year = Number(berlinDate(Date.now()).slice(0, 4));
  const vac = await api<{ rows: { user: UserDTO; summary: VacationSummary }[] }>(`/admin/vacation?year=${year}`, { fresh: true });
  const rest = new Map(vac.rows.map((x) => [x.user.id, x.summary]));
  const rules = await api<{ rules: WageRule[] }>('/admin/wage-rules', { fresh: true });
  el.innerHTML = `
    <h1>Mitarbeiter</h1>
    <ul class="user-list">${r.users
      .map((u) => {
        const s = rest.get(u.id);
        return `<li class="${u.active ? '' : 'inactive'}">
        <details><summary><strong>${esc(u.name)}</strong> (${esc(u.short)})${u.role === 'admin' ? ' · Admin' : ''}${u.jobTitle ? ` · ${esc(u.jobTitle)}` : ''}${u.employmentType === 'minijob' && u.role !== 'admin' ? ' · Minijob' : ''}${u.active ? '' : ' · deaktiviert'}
          ${!u.hasPin ? '<span class="badge wait">Einladung offen</span>' : ''}
          ${u.lockedUntil && u.lockedUntil > Date.now() ? '<span class="badge err">gesperrt</span>' : ''}
          ${s ? `<span class="muted small">· Resturlaub ${formatNumberDe(s.remaining, 1)} von ${formatNumberDe(s.entitlement, 1)}</span>` : ''}</summary>
          <form class="stack user-form" data-id="${u.id}">
            ${userFields(u)}
            <label class="check"><input type="checkbox" name="active" value="1" ${u.active ? 'checked' : ''}> aktiv</label>
            <button class="btn primary">Speichern</button>
          </form>
          ${u.inviteCode ? inviteBox(u.inviteCode) : ''}
          <button class="btn small" data-invite="${u.id}">${u.hasPin ? 'Neues Gerät / PIN vergessen: neue Einladung' : 'Einladung neu erzeugen'}</button>
          <a class="btn small" href="#/admin/eintraege/${u.id}/${monthOf(berlinDate(Date.now()))}">Einträge</a>
        </details></li>`;
      })
      .join('')}</ul>
    <details class="card"><summary><strong>Neuen Mitarbeiter anlegen</strong></summary>
      <form id="new-user" class="stack">
        ${userFields()}
        <button class="btn primary">Anlegen und Einladung erzeugen</button>
      </form>
      <div id="new-invite"></div>
    </details>
    <details class="card"><summary><strong>Mindestlohn &amp; Minijob-Grenze</strong></summary>
      <table class="grid small"><thead><tr><th>Jahr</th><th class="r">Mindestlohn</th><th class="r">Grenze/Monat</th></tr></thead>
      <tbody>${rules.rules.map((r) => `<tr><td>${r.year}</td><td class="r">${formatEuro(r.minWage)}</td><td class="r">${formatEuro(r.minijobLimit)}</td></tr>`).join('')}</tbody></table>
      <form id="wage-rule" class="row">
        <input name="year" type="number" min="2020" max="2100" placeholder="Jahr" required>
        <input name="minWage" type="number" step="0.01" min="0" placeholder="Mindestlohn €" required>
        <input name="minijobLimit" type="number" step="1" min="0" placeholder="Grenze €" required>
        <button class="btn">Speichern</button>
      </form>
    </details>
  `;
  const wr = $('#wage-rule', el) as HTMLFormElement;
  wr.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const v = formValues(wr);
    try {
      await api(`/admin/wage-rules/${v.year}`, { method: 'PUT', body: { minWage: Number(v.minWage), minijobLimit: Number(v.minijobLimit) } });
      toast('Gespeichert.');
      void renderUsers(el);
    } catch (err) {
      toast(errorText(err), 'error');
    }
  });
  el.querySelectorAll<HTMLFormElement>('.user-form').forEach((f) =>
    f.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      try {
        await api(`/admin/users/${f.dataset.id}`, { method: 'PATCH', body: userBody(f, true) });
        toast('Gespeichert.');
        void renderUsers(el);
      } catch (err) {
        toast(errorText(err), 'error');
      }
    }),
  );
  el.querySelectorAll<HTMLButtonElement>('[data-invite]').forEach((b) =>
    b.addEventListener('click', async () => {
      if (!confirm('Neue Einladung erzeugen? Die bisherige PIN und alle angemeldeten Geräte werden ungültig.')) return;
      try {
        const x = await api<{ inviteCode: string; inviteLink: string }>(`/admin/users/${b.dataset.invite}/invite`, { method: 'POST' });
        b.insertAdjacentHTML('beforebegin', inviteBox(x.inviteCode, x.inviteLink));
      } catch (err) {
        toast(errorText(err), 'error');
      }
    }),
  );
  const nf = $('#new-user', el) as HTMLFormElement;
  nf.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    try {
      const x = await api<{ inviteCode: string; inviteLink: string }>('/admin/users', { method: 'POST', body: userBody(nf, false) });
      $('#new-invite', el).innerHTML = inviteBox(x.inviteCode, x.inviteLink);
      nf.reset();
      toast('Mitarbeiter angelegt.');
    } catch (err) {
      toast(errorText(err), 'error');
    }
  });
}

function userFields(u?: AdminUser): string {
  return `
    <div class="row2">
      <label>Name<input name="name" value="${esc(u?.name ?? '')}" required maxlength="80"></label>
      <label>Kürzel<input name="short" value="${esc(u?.short ?? '')}" required maxlength="8"></label>
    </div>
    <div class="row2">
      <label>Soll-Std./Monat<input name="targetHoursMonth" type="number" step="0.5" min="0" value="${u?.targetHoursMonth ?? ''}"></label>
      <label>Urlaubstage/Jahr<input name="vacationDaysYear" type="number" step="0.5" min="0" value="${u?.vacationDaysYear ?? ''}" required></label>
    </div>
    <div class="row2">
      <label>Stundenlohn €<input name="hourlyWage" type="number" step="0.01" min="0" value="${u?.hourlyWage ?? ''}"></label>
      <label>Rolle<select name="role"><option value="employee">Mitarbeiter</option><option value="admin" ${u?.role === 'admin' ? 'selected' : ''}>Admin</option></select></label>
    </div>
    <div class="row2">
      <label>Beschäftigung<select name="employmentType">
        <option value="minijob">Minijob</option>
        <option value="sonstige" ${u?.employmentType === 'sonstige' ? 'selected' : ''}>sonstige</option></select></label>
      <label>Tätigkeit<input name="jobTitle" value="${esc(u?.jobTitle ?? '')}" maxlength="60" placeholder="z. B. Reinigungskraft"></label>
    </div>
    <label class="check"><input type="checkbox" name="showEarnings" value="1" ${u === undefined || u.showEarnings ? 'checked' : ''}> Verdienst in der App anzeigen</label>`;
}

function userBody(f: HTMLFormElement, withActive: boolean): Record<string, unknown> {
  const v = formValues(f);
  return {
    name: v.name, short: v.short, role: v.role,
    targetHoursMonth: v.targetHoursMonth, vacationDaysYear: v.vacationDaysYear, hourlyWage: v.hourlyWage,
    employmentType: v.employmentType, jobTitle: v.jobTitle, showEarnings: v.showEarnings === '1',
    ...(withActive ? { active: v.active === '1' } : {}),
  };
}

function inviteBox(code: string, link = `${location.origin}/#/einladung/${code}`): string {
  const text = `Hallo! Hier ist dein Zugang zur HubTime-Zeiterfassung: ${link} – einfach öffnen und eine PIN wählen. (Code: ${code})`;
  return `<div class="invite"><p>Einladungscode <strong class="code">${esc(code)}</strong> (14 Tage gültig)</p>
    <p class="small"><a href="${esc(link)}">${esc(link)}</a></p>
    <a class="btn small" href="sms:?&body=${encodeURIComponent(text)}">Per SMS senden</a>
    <a class="btn small" href="https://wa.me/?text=${encodeURIComponent(text)}" target="_blank" rel="noopener">WhatsApp</a></div>`;
}

// ---------- Anträge / Abwesenheiten ----------

async function renderRequests(el: HTMLElement): Promise<void> {
  const year = Number(berlinDate(Date.now()).slice(0, 4));
  const [open, all, users] = await Promise.all([
    api<{ absences: AbsenceDTO[] }>('/admin/absences?status=beantragt', { fresh: true }),
    api<{ absences: AbsenceDTO[] }>(`/admin/absences?year=${year}`, { fresh: true }),
    api<{ users: AdminUser[] }>('/admin/users', { fresh: true }),
  ]);
  const today = berlinDate(Date.now());
  el.innerHTML = `
    <h1>Urlaub &amp; Krankheit</h1>
    <h2>Offene Anträge</h2>
    ${open.absences.length ? `<ul class="abs-list">${open.absences
      .map((a) => `<li><div><strong>${esc(a.userName ?? '')}</strong> · Urlaub ${esc(period(a))} · ${formatNumberDe(a.days, 1)} Tage</div>
        ${a.note ? `<div class="muted small">${esc(a.note)}</div>` : ''}
        <div class="row"><button class="btn primary small" data-dec="${esc(a.id)}" data-ok="1">Genehmigen</button>
        <button class="btn small danger" data-dec="${esc(a.id)}" data-ok="0">Ablehnen</button></div></li>`)
      .join('')}</ul>` : '<p class="muted">Keine offenen Anträge.</p>'}
    <details class="card"><summary><strong>Abwesenheit für Mitarbeiter eintragen</strong> (z. B. telefonische Krankmeldung)</summary>
      <form id="adm-abs" class="stack">
        <label>Mitarbeiter<select name="userId">${users.users.filter((u) => u.active).map((u) => `<option value="${u.id}">${esc(u.name)}</option>`).join('')}</select></label>
        <div class="seg"><label><input type="radio" name="type" value="krank" checked> Krank</label>
          <label><input type="radio" name="type" value="urlaub"> Urlaub (direkt genehmigt)</label></div>
        <div class="row2"><label>Von<input type="date" name="startDate" value="${today}" required></label>
          <label>Bis<input type="date" name="endDate" value="${today}" required></label></div>
        <label class="check"><input type="checkbox" name="halfDay" value="1"> halber Tag</label>
        <label>Notiz<input name="note"></label>
        <button class="btn primary">Eintragen</button>
      </form></details>
    <h2>Alle Abwesenheiten ${year}</h2>
    <ul class="abs-list">${all.absences
      .map((a) => `<li class="st-${a.status}"><div><strong>${esc(a.userName ?? '')}</strong> · ${a.type === 'urlaub' ? 'Urlaub' : 'Krank'} ${esc(period(a))}</div>
        <div class="muted small">${formatNumberDe(a.days, 1)} Tage · ${esc(STATUS_LABEL[a.status])}${a.note ? ` · ${esc(a.note)}` : ''}</div>
        ${a.status === 'genehmigt' || a.status === 'gemeldet' ? `<button class="btn small" data-cancel="${esc(a.id)}">Stornieren</button>` : ''}</li>`)
      .join('')}</ul>
  `;
  el.querySelectorAll<HTMLButtonElement>('[data-dec]').forEach((b) =>
    b.addEventListener('click', async () => {
      await api(`/admin/absences/${b.dataset.dec}/decision`, { method: 'POST', body: { approve: b.dataset.ok === '1' } });
      toast(b.dataset.ok === '1' ? 'Genehmigt.' : 'Abgelehnt.');
      void renderRequests(el);
    }),
  );
  el.querySelectorAll<HTMLButtonElement>('[data-cancel]').forEach((b) =>
    b.addEventListener('click', async () => {
      if (!confirm('Abwesenheit stornieren?')) return;
      await api(`/absences/${b.dataset.cancel}`, { method: 'DELETE' });
      void renderRequests(el);
    }),
  );
  const f = $('#adm-abs', el) as HTMLFormElement;
  f.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const v = formValues(f);
    try {
      await api('/admin/absences', {
        method: 'POST',
        body: { userId: Number(v.userId), type: v.type, startDate: v.startDate, endDate: v.endDate, halfDay: v.halfDay === '1', note: v.note },
      });
      toast('Eingetragen.');
      void renderRequests(el);
    } catch (err) {
      toast(errorText(err), 'error');
    }
  });
}

// ---------- Tätigkeiten ----------

interface ActivityAdmin {
  id: number;
  name: string;
  active: number;
  showOnHome: number;
  visitGroup: string | null;
  intervalDays: number | null;
  recentDays: number;
}

async function renderActivities(el: HTMLElement): Promise<void> {
  const r = await api<{ activities: ActivityAdmin[] }>('/admin/activities', { fresh: true });
  el.innerHTML = `
    <h1>Tätigkeiten / Objekte</h1>
    <ul class="user-list">${r.activities
      .map((a) => `<li class="${a.active ? '' : 'inactive'}"><details>
        <summary><strong>${esc(a.name)}</strong>${a.active ? '' : ' · ausgeblendet'}
          <span class="muted small">· ${a.showOnHome ? `Kachel „${esc(a.visitGroup || a.name)}"` : 'keine Kachel'}${a.intervalDays ? ` · alle ${a.intervalDays} Tage` : ''}</span></summary>
        <form class="stack act-form" data-id="${a.id}">
          <label class="check"><input type="checkbox" name="showOnHome" value="1" ${a.showOnHome ? 'checked' : ''}> „Letzter Besuch" auf der Startseite zeigen</label>
          <label>Kachel (gleicher Name = gemeinsame Kachel, z. B. „Hub")<input name="visitGroup" value="${esc(a.visitGroup ?? '')}" maxlength="40" placeholder="${esc(a.name)}"></label>
          <div class="row2">
            <label>Soll: mindestens alle … Tage<input name="intervalDays" type="number" min="1" max="365" value="${a.intervalDays ?? ''}" placeholder="kein Soll"></label>
            <label>Hinweis „war erst … da" bis … Tage<input name="recentDays" type="number" min="0" max="30" value="${a.recentDays}"></label>
          </div>
          <div class="row"><button class="btn primary small">Speichern</button>
            <button type="button" class="btn small" data-act="${a.id}" data-active="${a.active}">${a.active ? 'In Auswahl ausblenden' : 'In Auswahl einblenden'}</button></div>
        </form></details></li>`)
      .join('')}</ul>
    <form id="new-act" class="row"><input name="name" placeholder="Neue Tätigkeit, z. B. Ferienwohnung" required maxlength="60">
      <button class="btn primary">Hinzufügen</button></form>
    <p class="muted small">Ausgeblendete Tätigkeiten bleiben in alten Einträgen erhalten. Ampel: grün im Soll, gelb höchstens
    1 Tag bis fällig, rot überfällig. Bei gemeinsamer Kachel gilt das kürzeste Intervall.</p>`;
  el.querySelectorAll<HTMLButtonElement>('[data-act]').forEach((b) =>
    b.addEventListener('click', async () => {
      await api(`/admin/activities/${b.dataset.act}`, { method: 'PATCH', body: { active: b.dataset.active !== '1' } });
      void renderActivities(el);
    }),
  );
  el.querySelectorAll<HTMLFormElement>('.act-form').forEach((f) =>
    f.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      const v = formValues(f);
      try {
        await api(`/admin/activities/${f.dataset.id}`, {
          method: 'PATCH',
          body: { showOnHome: v.showOnHome === '1', visitGroup: v.visitGroup, intervalDays: v.intervalDays || null, recentDays: Number(v.recentDays || 0) },
        });
        toast('Gespeichert.');
        void renderActivities(el);
      } catch (err) {
        toast(errorText(err), 'error');
      }
    }),
  );
  const f = $('#new-act', el) as HTMLFormElement;
  f.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    try {
      await api('/admin/activities', { method: 'POST', body: { name: formValues(f).name } });
      void renderActivities(el);
    } catch (err) {
      toast(errorText(err), 'error');
    }
  });
}

// ---------- Protokoll ----------

async function renderAudit(el: HTMLElement): Promise<void> {
  const r = await api<{ audit: { id: number; at: number; actorName: string | null; action: string; entity: string; entityId: string | null; after: Record<string, unknown> | null; before: Record<string, unknown> | null }[] }>(
    '/admin/audit?limit=300', { fresh: true },
  );
  const ENTITY: Record<string, string> = {
    entry: 'Eintrag', absence: 'Abwesenheit', user: 'Mitarbeiter', month: 'Monat', activity: 'Tätigkeit',
    waste: 'Mülltonne', appliance: 'Gerätelauf', 'appliance-config': 'Gerät', wage_rule: 'Mindestlohn/Grenze',
  };
  el.innerHTML = `
    <h1>Änderungsprotokoll</h1>
    <p class="muted small">Die letzten 300 Änderungen. Details je Eintrag über „Bearbeiten".</p>
    <div class="table-wrap"><table class="grid small">
      <thead><tr><th>Zeitpunkt</th><th>Wer</th><th>Was</th><th>Details</th></tr></thead>
      <tbody>${r.audit
        .map((a) => {
          const x = (a.after ?? a.before ?? {}) as Record<string, unknown>;
          const detail = a.entity === 'entry'
            ? `${esc(x.date)} ${esc(x.start)}–${esc(x.end ?? '')} ${esc(x.activity ?? '')}`
            : a.entity === 'month' ? esc(`${x.month ?? ''}`) : a.entity === 'absence' ? esc(`${x.type ?? ''} ${x.startDate ?? ''}–${x.endDate ?? ''}`) : esc(`${x.name ?? a.entityId ?? ''}`);
          return `<tr><td class="nowrap">${esc(new Date(a.at).toLocaleString('de-DE'))}</td><td>${esc(a.actorName ?? 'System')}</td>
            <td>${esc(ENTITY[a.entity] ?? a.entity)} ${esc(a.action)}</td><td>${detail}</td></tr>`;
        })
        .join('')}</tbody></table></div>`;
}

// ---------- Geräte ----------

const RUN_STATUS: Record<string, string> = { laeuft: 'läuft', fertig: 'fertig, nicht ausgeräumt', erledigt: 'ausgeräumt', abgebrochen: 'abgebrochen' };

async function renderAppliancesAdmin(el: HTMLElement, q: URLSearchParams): Promise<void> {
  const today = berlinDate(Date.now());
  const from = q.get('von') ?? `${today.slice(0, 7)}-01`;
  const to = q.get('bis') ?? today;
  const applianceId = q.get('geraet') ?? '';
  const [list, hist] = await Promise.all([
    api<{ appliances: ApplianceAdminDTO[] }>('/admin/appliances', { fresh: true }),
    api<{ runs: RunDTO[] }>(`/admin/appliances/history?from=${from}&to=${to}${applianceId ? `&applianceId=${applianceId}` : ''}`, { fresh: true }),
  ]);
  const name = new Map(list.appliances.map((a) => [a.id, `${a.icon} ${a.name}`]));
  const now = Date.now();
  const t = (ms: number | null) => (ms ? `${new Date(ms).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} ${berlinTime(ms)}` : '–');
  el.innerHTML = `
    <h1>Geräte</h1>
    <ul class="user-list">${list.appliances
      .map((a) => `<li class="${a.active ? '' : 'inactive'}"><details><summary>${esc(a.icon)} <strong>${esc(a.name)}</strong>
        · Standard ${formatHM(a.defaultMinutes)} Std.${a.hasSensor ? ' · Steckdose' : ''}${a.active ? '' : ' · ausgeblendet'}</summary>
        <form class="stack appl-form" data-id="${a.id}">${applianceFields(a)}
          <label class="check"><input type="checkbox" name="active" value="1" ${a.active ? 'checked' : ''}> aktiv</label>
          <button class="btn primary">Speichern</button></form></details></li>`)
      .join('')}</ul>
    <details class="card"><summary><strong>Gerät hinzufügen</strong></summary>
      <form id="new-appl" class="stack">${applianceFields()}<button class="btn primary">Hinzufügen</button></form></details>

    <h2>Protokoll</h2>
    <form id="hist" class="row">
      <select name="geraet"><option value="">alle Geräte</option>${list.appliances
        .map((a) => `<option value="${a.id}" ${String(a.id) === applianceId ? 'selected' : ''}>${esc(a.name)}</option>`).join('')}</select>
      <input type="date" name="von" value="${esc(from)}" aria-label="von">
      <input type="date" name="bis" value="${esc(to)}" aria-label="bis">
      <button class="btn">Anzeigen</button>
    </form>
    <div class="table-wrap"><table class="grid small">
      <thead><tr><th>Gerät</th><th>Start</th><th>Dauer</th><th>Status</th><th>Wer</th></tr></thead>
      <tbody>${hist.runs
        .map((r) => {
          const phase = runPhase(r, now);
          const who = [
            r.startedByName ? `Start: ${esc(r.startedByName)}` : 'Start: Steckdose',
            r.sensorConfirmedAt && r.source === 'manual' ? 'Steckdose ✓' : '',
            r.extendedMinutes ? `+${r.extendedMinutes} min` : '',
            r.emptiedAt ? `ausgeräumt: ${esc(r.emptiedByName ?? 'automatisch')} ${t(r.emptiedAt)}` : '',
            r.cancelledAt ? `abgebrochen: ${esc(r.cancelledByName ?? '?')} ${t(r.cancelledAt)}${r.cancelReason ? ` („${esc(r.cancelReason)}")` : ''}` : '',
          ].filter(Boolean).join('<br>');
          return `<tr><td>${esc(name.get(r.applianceId) ?? '?')}</td><td class="nowrap">${t(r.startedAt)}</td>
            <td class="nowrap">${formatHM(r.plannedMinutes + r.extendedMinutes)}${r.finishedAt ? `<br><small>fertig ${berlinTime(r.finishedAt)}</small>` : ''}</td>
            <td>${esc(RUN_STATUS[phase])}${r.finishedSource === 'sensor' ? '<br><small>Ende laut Steckdose</small>' : ''}</td><td>${who}</td></tr>`;
        })
        .join('') || '<tr><td colspan="5" class="muted">Keine Läufe im Zeitraum.</td></tr>'}</tbody>
    </table></div>`;

  const body = (f: HTMLFormElement) => {
    const v = formValues(f);
    return {
      name: v.name, icon: v.icon, defaultMinutes: Number(v.defaultMinutes), programs: parsePrograms(v.programs),
      sensorKey: v.sensorKey || null, sensorHost: v.sensorHost || null,
      startWatts: Number(v.startWatts), startSeconds: Number(v.startSeconds), endWatts: Number(v.endWatts), endMinutes: Number(v.endMinutes),
      ...(f.id === 'new-appl' ? {} : { active: v.active === '1' }),
    };
  };
  el.querySelectorAll<HTMLFormElement>('.appl-form').forEach((f) =>
    f.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      try {
        await api(`/admin/appliances/${f.dataset.id}`, { method: 'PATCH', body: body(f) });
        toast('Gespeichert.');
        void renderAppliancesAdmin(el, q);
      } catch (err) {
        toast(errorText(err), 'error');
      }
    }),
  );
  const nf = $('#new-appl', el) as HTMLFormElement;
  nf.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    try {
      await api('/admin/appliances', { method: 'POST', body: body(nf) });
      toast('Gerät hinzugefügt.');
      void renderAppliancesAdmin(el, q);
    } catch (err) {
      toast(errorText(err), 'error');
    }
  });
  const hf = $('#hist', el) as HTMLFormElement;
  hf.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const v = formValues(hf);
    location.hash = `#/admin/geraete/${encodeURIComponent(new URLSearchParams(v).toString())}`;
  });
}

function applianceFields(a?: ApplianceAdminDTO): string {
  return `
    <div class="row2">
      <label>Name<input name="name" value="${esc(a?.name ?? '')}" required maxlength="40"></label>
      <label>Symbol<input name="icon" value="${esc(a?.icon ?? '')}" maxlength="8" placeholder="z. B. 🧺"></label>
    </div>
    <label>Standard-Dauer (Minuten)<input name="defaultMinutes" type="number" min="1" max="600" value="${a?.defaultMinutes ?? 90}" required></label>
    <label>Programme (Name=Minuten, durch Komma getrennt)<input name="programs" value="${esc(a ? formatPrograms(a.programs) : '')}" placeholder="Kurz=45, Normal=90"></label>
    <details><summary>Mess-Steckdose (optional)</summary>
      <div class="stack" style="margin-top:10px">
        <div class="row2">
          <label>Sensor-Kennung (Webhook)<input name="sensorKey" value="${esc(a?.sensorKey ?? '')}" placeholder="shellyplusplugs-…"></label>
          <label>Adresse im Hausnetz (Abfrage)<input name="sensorHost" value="${esc(a?.sensorHost ?? '')}" placeholder="192.168.178.60"></label>
        </div>
        <div class="row2">
          <label>Start ab Watt<input name="startWatts" type="number" step="0.1" min="0" value="${a?.startWatts ?? 10}"></label>
          <label>für Sekunden<input name="startSeconds" type="number" min="0" value="${a?.startSeconds ?? 60}"></label>
        </div>
        <div class="row2">
          <label>Ende unter Watt<input name="endWatts" type="number" step="0.1" min="0" value="${a?.endWatts ?? 5}"></label>
          <label>für Minuten<input name="endMinutes" type="number" min="0" value="${a?.endMinutes ?? 10}"></label>
        </div>
        <p class="muted small">Spülmaschinen trocknen lange fast ohne Strom – dort 30 Minuten oder mehr wählen.</p>
      </div>
    </details>`;
}
